import { DateTime } from "luxon";
import { GetterTree, ActionTree, MutationTree, Commit } from "vuex";
import {
  CalendarBoxConfig,
  CalendarPropertyView,
  CalendarRatePlanAccomodationUnitTypeView,
  CalendarUnitTypeView,
  CalendarUnitView,
  RatePlan,
  ratePlanFields,
} from "@/components/calendar/CalendarTypes";
import Vue from "vue";
import { isArray, isEmpty } from "lodash";
import { ListModel } from "@/shared/models/listModel";
import * as _ from "lodash";
import router from "@/router";

// #region types
export type CalendarNumOfDays = "week" | "two-weeks" | "month" | "max";
export type CalendarRowHeight = "small" | "standard" | "big";
export type ReservationLineOption =
  | "reservationHolderName"
  | "totalPrice"
  | "otachannelName"
  | "optionExpirationDate"
  | "numberOfNights";
export type CalendarColorByOptions = "status" | "salesChannel";
export type CalendarSettings = {
  numOfDays: CalendarNumOfDays;
  rowHeight: CalendarRowHeight;
  showUnits: boolean;
  showRatePlans: boolean;
  autoExpandRatePlans: boolean;
  showOBRatePlans: boolean;
  showRestrictions: boolean;
  showRatePlanFields: string[];
  showListRatePlans: number[];
  userId: number;
  isForAvailabilitySearchCalendar: boolean;
  showInputPrice: boolean;
  tenantId: number;
  reservationColoringBy: CalendarColorByOptions;
};

export type BlockedAvailabilitiesModel = {
  propertyId: number;
  calculationInProgress: boolean;
  blockedAvailabilities: BlockedAvailabilityModel[];
}

export type BlockedAvailabilityModel = {
  unitTypeId: number;
  //ratePlanId: number;
  date: string;
  ratePlansAccomodationUnitTypeId: number;
}

export const defaultSettings = {
  numOfDays: "month",
  rowHeight: "small",
  showUnits: true,
  showRatePlans: false,
  autoExpandRatePlans: false,
  showOBRatePlans: false,
  showRestrictions: false,
  showRatePlanFields: ratePlanFields.map((x) => x.key),
  showListRatePlans: [],
  userId: 0,
  isForAvailabilitySearchCalendar: false,
  showInputPrice: false,
  tenantId: 0,
  reservationColoringBy: "status"
} as CalendarSettings;
// #endregion

const storageKey = "calendar.settings";

let _settings: CalendarSettings[] = [defaultSettings];
const saved = localStorage.getItem(storageKey);
if (saved) {
  try {
    _settings = JSON.parse(saved) as CalendarSettings[];
  } catch {
    localStorage.removeItem("calendar.settings");
  }
  if (isEmpty(_settings) || !isArray(_settings)) {
    localStorage.removeItem("calendar.settings");
    _settings = [defaultSettings];
  }
}

const initialState = {
  loading: { hierarchy: false, reservationsAndUnits: false },
  loadingAvailabilityAndReservationsForPropertyIds: [] as number[],
  currentDay: DateTime.local(),
  availabilityTimelineOpenOnClick: false,
  settingsOpen: false,
  calendarNeedsRefresh: false,
  settings: _settings,
  properties: [] as CalendarPropertyView[],
  months: "",
  nonOBRatePlans: [] as RatePlan[],
  propertiesWithBlockedDatesCalculationInProgress: [] as number[],
  leftEdgeDay: DateTime.local(),
  rightEdgeDay: DateTime.local(),
  availabilities: [] as any[],
  reservations: [] as any[],
  blockedAvailabilities: [] as BlockedAvailabilityModel[]
};

let isBlockedAvailabilitiesCheckRunning = false;

function getBlockedAvailabilities(state: typeof initialState, commit: Commit, usesLazyLoadedCalendar: boolean) {
  isBlockedAvailabilitiesCheckRunning = true;

  const propertyIds = state.propertiesWithBlockedDatesCalculationInProgress.join(',');
  const from = state.leftEdgeDay.toISO().substring(0, 10);
  const to = state.rightEdgeDay.toISO().substring(0, 10);

  Vue.axios.get<BlockedAvailabilitiesModel[]>(
    "calendar/GetBlockedAvailabilities",
    {
      params: {
        from: from,
        to: to,
        propertyIds: propertyIds
      },
    }
  ).then(res => {
    commit("mergeBlockedAvailabilities", {
      blockedAvailabilities: res.data,
      usesLazyLoadedCalendar: usesLazyLoadedCalendar
    });

    if (router && router.currentRoute && router.currentRoute.name === "Calendar" && state.propertiesWithBlockedDatesCalculationInProgress.length !== 0) {
      setTimeout(() => {
        getBlockedAvailabilities(state, commit, usesLazyLoadedCalendar);
      }, 5000);
    }
    else {
      isBlockedAvailabilitiesCheckRunning = false;
    }
  });
}

const state = { ...initialState };

const getters: GetterTree<typeof state, any> = {
  isLoading: (s) => s.loading.hierarchy || s.loading.reservationsAndUnits || (s.loadingAvailabilityAndReservationsForPropertyIds && s.loadingAvailabilityAndReservationsForPropertyIds.length > 0),
  loadingAvailabilityAndReservationsForPropertyIds: (s) => s.loadingAvailabilityAndReservationsForPropertyIds,
  currentDay: (s) => s.currentDay.startOf("day"),
  availabilityTimelineOpenOnClick: (s) => s.availabilityTimelineOpenOnClick,
  settingsOpen: (s) => s.settingsOpen,
  settings: (s) => s.settings,
  calendarNeedsRefresh: (s) => s.calendarNeedsRefresh,
  properties: (s) => s.properties,
  months: (s) => s.months,
  nonOBRatePlans: (s) => s.nonOBRatePlans,
  propertiesWithBlockedDatesCalculationInProgress: (s) => s.propertiesWithBlockedDatesCalculationInProgress,
  availabilitiesAndReservations: (s) => {
    return {
      availabilities: s.availabilities,
      reservations: s.reservations,
      blockedAvailabilities: s.blockedAvailabilities
    }
  }
};

const actions: ActionTree<typeof state, any> = {
  setEdgeDays: ({ commit }, payload: { left: DateTime; right: DateTime }) => {
    commit("setEdgeDays", payload);
  },
  setMonths: ({ commit }, payload: string) => {
    commit("setMonths", payload);
  },
  goToDay: ({ commit }, payload: DateTime) => {
    commit("setDay", payload.startOf("day"));
  },
  toggleAvailabilityTimelineOpenOnClick: ({ commit }, payload: boolean) => {
    commit("toggleAvailabilityTimelineOpenOnClick", payload);
  },
  openSettings: ({ commit }) => {
    commit("settingsMenu", true);
  },
  closeSettings: ({ commit }) => {
    commit("settingsMenu", false);
  },
  updateSettings: ({ commit }, payload: CalendarSettings) => {
    commit("updateSettings", payload);
  },
  setCalendarNeedsRefresh({ commit }, payload: boolean) {
    commit("setCalendarNeedsRefresh", payload);
  },
  refreshNonOBRatePlans({ commit }) {
    return Vue.axios
      .get<ListModel<RatePlan>>("/calendar/getListNonOBRatePlans")
      .then((res) => {
        commit("setNonOBRatePlans", res.data.result);
      });
  },
  fetchHierarchy: async (
    { commit },
    payload: { config: CalendarBoxConfig; accomodationUnitTypeIds?: number[] }
  ): Promise<CalendarPropertyView[]> => {
    commit("setLoadingHierarchy", true);

    try {
      const propertiesPromise = Vue.axios.get<CalendarPropertyView[]>(
        "calendar/properties",
        {
          params: {
            accomodationUnitTypeIds: payload.accomodationUnitTypeIds?.join(',')
          }
        }
      );

      const accomodationUnitsPromise = Vue.axios.get<CalendarUnitView[]>(
        "calendar/units",
        {
          params: {
            accomodationUnitTypeIds: payload.accomodationUnitTypeIds?.join(',')
          }
        }
      );

      const [property, units] = await Promise.all([
        propertiesPromise,
        accomodationUnitsPromise
      ]);
      const data = property.data
        .filter((p) => p.unitTypes.length > 0)
        .map(
          (p) =>
            new CalendarPropertyView(
              p.id,
              p.name,
              p.shortName,
              true,
              p.unitTypes.map(
                (ut, uti) =>
                  new CalendarUnitTypeView(
                    ut.id,
                    ut.name,
                    ut.baseBedsCapacity,
                    ut.extraBedsCapacity,
                    true,
                    payload.config.cell.h,
                    payload.config.cell.h * uti,
                    ut.ratePlansHeight,
                    ut.ratePlans
                      // sort by parentId/Id, then by derived status, then by id
                      // keeps everything rate plans sorted by ID with derived plans grouped under their parent
                      // moved to server-side sorting
                      // .sort((a, b) => (a.parentRatePlanId ? a.parentRatePlanId : a.id) > (b.parentRatePlanId ? b.parentRatePlanId : b.id) ? 1
                      //   : (a.parentRatePlanId ? a.parentRatePlanId : a.id) < (b.parentRatePlanId ? b.parentRatePlanId : b.id) ? -1
                      //     : (a.parentRatePlanId == b.parentRatePlanId && a.isOBSpecific && b.isOBSpecific 
                      //       && ((a.numberOfPersons ?? 0) > (b.numberOfPersons ?? 0))) ? 1
                      //       : (a.parentRatePlanId == b.parentRatePlanId && a.isOBSpecific && b.isOBSpecific 
                      //         && ((a.numberOfPersons ?? 0) < (b.numberOfPersons ?? 0))) ? -1
                      //         : (a.parentRatePlanId == b.parentRatePlanId && a.isOBSpecific && b.isOBSpecific 
                      //           && ((a.minStay ?? 0) > (b.minStay ?? 0))) ? 1
                      //           : (a.parentRatePlanId == b.parentRatePlanId && a.isOBSpecific && b.isOBSpecific 
                      //             && ((a.minStay ?? 0) < (b.minStay ?? 0))) ? -1
                      //     : a.isDerived && !b.isDerived ? 1
                      //       : !a.isDerived && b.isDerived ? -1
                      //         : a.id > b.id ? 1
                      //           : a.id < b.id ? -1
                      //             : 0)
                      .map(
                        (rp) =>
                          new CalendarRatePlanAccomodationUnitTypeView(
                            rp.id,
                            rp.ratePlanId,
                            rp.parentId,
                            rp.parentRatePlanId,
                            //rp.grandparentRatePlanId,
                            rp.name,
                            rp.currencyId,
                            rp.currencyCode,
                            rp.isDerived,
                            rp.isSpecialOrPackageOffer,
                            rp.isDefault,
                            rp.derivedFrom,
                            false,
                            payload.config.cell.h,
                            0,
                            [],
                            rp.minPersons,
                            rp.maxPersons,
                            rp.minStay,
                            rp.maxStay,
                            false
                          )
                      ),
                    units.data
                      .filter((u) => u.typeId === ut.id)
                      .map(
                        (u) =>
                          new CalendarUnitView(
                            u.id,
                            u.typeId,
                            u.statusId,
                            u.maintenanceAssignedToTenantUserId,
                            u.name,
                            u.shortName,
                            u.note,
                            payload.config.cell.hl,
                            []
                          )
                      ),
                    []
                  )
              ),
              [],
              payload.config.cell
            )
        );

      commit("setProperties", data);
      return data;
    } finally {
      commit("setLoadingHierarchy", false);
    }
  },
  resetAviailabilityAndReservations: ({ commit }) =>
    commit("resetAviailabilityAndReservations"),
  removeAvailabilitiesForProperties: ({ commit }, propertyIds: number[]) => {
    commit("removeAvailabilities", propertyIds);
  },
  addAvailabilities: ({ commit }, payload: any[]) => {
    commit("addAvailabilities", payload);
  },
  fetchAviailabilityAndReservations: async (
    { commit, dispatch },
    payload: { from: DateTime; to: DateTime; propertyIds: number[]; accomodationUnitTypeIds?: number[] }
  ): Promise<[any[], any[]]> => {
    if (payload.propertyIds.length > 0) {
      commit("setLoadingAvailabilityAndReservationsForPropertyIds", payload.propertyIds);
    }
    else {
      commit("setLoadingReservationsAndUnits", true);
    }

    try {
      const availabilitiesPromise = Vue.axios.get<any[]>(
        "calendar/availability",
        {
          params: {
            from: payload.from.toISODate(),
            to: payload.to.toISODate(),
            propertyIds: payload.propertyIds?.join(','),
            accomodationUnitTypeIds: payload.accomodationUnitTypeIds?.join(',')
          },
        }
      );
      const reservationsPromise = Vue.axios.get<any[]>(
        "calendar/reservations",
        {
          params: {
            from: payload.from.toISODate(),
            to: payload.to.toISODate(),
            propertyIds: payload.propertyIds?.join(','),
            accomodationUnitTypeIds: payload.accomodationUnitTypeIds?.join(',')
          },
        }
      );

      const [availabilities, reservations] = await Promise.all([
        availabilitiesPromise,
        reservationsPromise,
      ]);

      commit("removeReservationsIntersectingPeriod", {
        from: payload.from,
        to: payload.to,
        propertyIds: payload.propertyIds
      });
      commit("mergeAvailabilitiesAndReservations", {
        availabilities: availabilities.data,
        reservations: reservations.data,
        propertyIds: payload.propertyIds
      });

      dispatch("startBlockedAvailabilitiesCheck", payload.propertyIds);

      return [availabilities.data, reservations.data];
    } finally {
      commit("setLoadingReservationsAndUnits", false);
    }
  },
  startBlockedAvailabilitiesCheck: async (
    { commit, rootGetters, state },
    payload: number[]
  ) => {
    // skip blocked availabilities querying if tenant doesn't use availability blocker calculations
    if (rootGetters['appParams/tenantUsesAvailabilityBlockerCalculations']) {
      commit("addPropertiesToListForChecking", payload);

      if (!isBlockedAvailabilitiesCheckRunning) {
        const usesLazyLoadedCalendar = rootGetters['appParams/tenantUsesLazyLoadedCalendar'] as boolean;
        getBlockedAvailabilities(state, commit, usesLazyLoadedCalendar);
      }
    }
  },
  startBlockedAvailabilitiesCheckForUnitTypes: async (
    { dispatch, state },
    payload: number[]
  ) => {
    dispatch("startBlockedAvailabilitiesCheck", state.properties.filter(x => x.unitTypes.some(ut => payload.includes(ut.id))).map(x => x.id));
  }
};

const mutations: MutationTree<typeof state> = {
  reset: (state) => {
    Object.assign(state, initialState);
  },
  setEdgeDays: (state, payload: { left: DateTime; right: DateTime }) => { state.leftEdgeDay = payload.left; state.rightEdgeDay = payload.right; },
  setMonths: (state, payload: string) => (state.months = payload),
  setDay: (state, payload: DateTime) => (state.currentDay = payload),
  toggleAvailabilityTimelineOpenOnClick: (state, payload: boolean) => (state.availabilityTimelineOpenOnClick = payload),
  settingsMenu: (state, payload: boolean) => (state.settingsOpen = payload),
  setCalendarNeedsRefresh: (state, payload: boolean) => (state.calendarNeedsRefresh = payload),
  updateSettings: (state, payload: CalendarSettings) => {
    const x = state.settings?.findIndex(x => x.userId == payload.userId && x.tenantId == payload.tenantId && (x.isForAvailabilitySearchCalendar ?? false) == payload.isForAvailabilitySearchCalendar);
    if (x !== undefined && x !== -1) {
      state.settings?.splice(x, 1, payload);
    }
    else {
      state.settings?.push(payload);
    }

    const stringified = JSON.stringify(state.settings);
    const saved = localStorage.getItem(storageKey);
    if (stringified != saved) {
      localStorage.setItem(storageKey, stringified);
    }
  },
  setLoadingHierarchy: (state, payload: boolean) => {
    state.loading.hierarchy = payload;
  },
  setLoadingReservationsAndUnits: (state, payload: boolean) => {
    state.loading.reservationsAndUnits = payload;
  },
  setProperties: (state, payload: CalendarPropertyView[]) => {
    state.properties = payload;
  },
  setNonOBRatePlans: (state, value) => {
    state.nonOBRatePlans = value;
  },
  resetAviailabilityAndReservations: (state) => {
    state.availabilities = [];
    state.reservations = [];
  },
  removeReservationsIntersectingPeriod: (
    state,
    payload: { from: DateTime; to: DateTime; propertyIds: number[] }
  ) => {
    const fromCorrected = new Date(Date.UTC(payload.from.toJSDate().getFullYear(), payload.from.toJSDate().getMonth(), payload.from.toJSDate().getDate()));
    const toCorrected = new Date(Date.UTC(payload.to.toJSDate().getFullYear(), payload.to.toJSDate().getMonth(), payload.to.toJSDate().getDate()));
    const [f, t] = [fromCorrected, toCorrected].map(x => x.toISOString()).map(x => x.substring(0, 10))

    // for normal loading, remove all reservations intersecting the queried period for refreshing
    if (payload.propertyIds.length == 0) {
      state.reservations = [...state.reservations.filter(x => (x.departureDate.substring(0, 10) < f || x.arrivalDate.substring(0, 10) > t))];
    }
    // for lazy loading, remove reservations intersecting the queried period only for properties being loaded
    else {
      const unitIds = state.properties.filter(x => payload.propertyIds?.includes(x.id)).flatMap(x => x.unitTypes.flatMap(e => e.units)).map(x => x.id);
      state.reservations = [...state.reservations.filter(x => !unitIds.includes(x.accomodationUnitId) || (x.departureDate.substring(0, 10) < f || x.arrivalDate.substring(0, 10) > t))];
    }
  },
  removeAvailabilities: (state, propertyIds: number[]) => {
    const unitTypeIds = state.properties.filter(x => propertyIds.includes(x.id)).flatMap(x => x.unitTypes).map(x => x.id);
    state.availabilities = [...state.availabilities.filter(x => !unitTypeIds.includes(x.unitTypeId))];
  },
  addAvailabilities: (state, payload: any[]) => {
    const old = [...state.availabilities];
    const fresh = [...payload];
    state.availabilities = _.uniqBy([...fresh, ...old], x => `${x.unitTypeId}_${x.ratePlansAccomodationUnitTypeId}_${x.date.substring(0, 10)}`);
  },
  mergeAvailabilitiesAndReservations: (
    state,
    payload: { availabilities: any[]; reservations: any[]; propertyIds: number[] }
  ) => {
    const old = [...state.availabilities];
    const fresh = [...payload.availabilities];

    state.availabilities = _.uniqBy([...fresh, ...old], x => `${x.unitTypeId}_${x.ratePlansAccomodationUnitTypeId}_${x.date.substring(0, 10)}`);
    state.reservations = [...payload.reservations, ...state.reservations];

    state.loadingAvailabilityAndReservationsForPropertyIds = [...state.loadingAvailabilityAndReservationsForPropertyIds.filter(x => !payload.propertyIds.includes(x))];
  },
  setLoadingAvailabilityAndReservationsForPropertyIds: (state, payload: number[]) => {
    state.loadingAvailabilityAndReservationsForPropertyIds = [...state.loadingAvailabilityAndReservationsForPropertyIds, ...payload];
  },
  addPropertiesToListForChecking: (state, payload: number[]) => {
    const newIds = payload.filter(x => !state.propertiesWithBlockedDatesCalculationInProgress.includes(x))
    state.propertiesWithBlockedDatesCalculationInProgress = [...state.propertiesWithBlockedDatesCalculationInProgress, ...newIds];
  },
  mergeBlockedAvailabilities: (state, payload: { blockedAvailabilities: BlockedAvailabilitiesModel[]; usesLazyLoadedCalendar: boolean }) => {
    state.propertiesWithBlockedDatesCalculationInProgress = payload.blockedAvailabilities.filter(x => x.calculationInProgress)
      .map(x => x.propertyId).slice();

    const propertiesWithoutCalculationsInprogress = payload.blockedAvailabilities.filter(x => !x.calculationInProgress);

    // if tenant doesn't use lazy loaded calendar then we need to keep the blockers saved
    if (!payload.usesLazyLoadedCalendar) {
      const fresh = propertiesWithoutCalculationsInprogress.flatMap(e => e.blockedAvailabilities);
      const old = state.blockedAvailabilities ?? [];
      state.blockedAvailabilities = _.uniqBy([...fresh, ...old], x => `${x.unitTypeId}_${x.ratePlansAccomodationUnitTypeId}_${x.date.substring(0, 10)}`);
    }

    for (const p of state.properties) {
      const blockedDates = propertiesWithoutCalculationsInprogress.find(x => x.propertyId === p.id);
      if (blockedDates) {
        p.mergeBlockedAvailabilities(blockedDates.blockedAvailabilities);
      }
    }
  }
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations,
};
